<template>
  <v-dialog
    v-if="useLegacyCookieConsent"
    v-model="showDialog"
    width="600"
    persistent
  >
    <v-card>
      <v-card-title class="pa-0">
        <v-toolbar
          color="primary"
          dark
          flat
        >
          <v-toolbar-title>{{ t('legal.cookie-consent.title') }}</v-toolbar-title>
          <v-spacer />
        </v-toolbar>
      </v-card-title>

      <v-card-text class="pt-0 pb-2 pl-3 pr-3 mt-4 pt-sm-0 pr-sm-6 pb-sm-5 pl-sm-6">
        <p>
          {{ t('legal.cookie-consent.description') }}
        </p>
        <p>
          {{ t('legal.cookie-consent.notice', { platformName: readablePlatformName }) }}
        </p>

        <p class="mb-0 mb-sm-2">
          <span v-if="useIubenda">
            <IubendaDocumentLink type="privacyPolicy" />
            <IubendaConsentLink class="ml-4" />
          </span>
          <template v-else>
            <a
              :href="publicDataPrivacyUrl"
              target="_blank"
              rel="noopener noreferrer"
              >{{ t('legal.topic.privacy.title') }}</a
            >
            <a
              :href="imprintUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="ml-4"
              >{{ t('legal.topic.imprint.title') }}</a
            >
          </template>
        </p>
        <v-list class="checkbox-list">
          <v-list-item
            v-for="cookieType in cookieTypes"
            :key="cookieType.value"
            class="ma-0 pa-0 flex align-center"
          >
            <template #prepend>
              <v-checkbox
                v-model="choice"
                :class="cookieType.showDescription && 'top-aligned'"
                color="primary"
                :value="cookieType.value"
                :readonly="cookieType.readOnly"
                @update:model-value="() => handleCheckboxChange(cookieType)"
              />
            </template>

            <template #default>
              <div :class="['v-list-item__content', cookieType.showDescription && 'top-aligned']">
                <div class="v-list-item-title">{{ cookieType.label }}</div>
                <v-expand-transition>
                  <p
                    v-if="cookieType.showDescription"
                    class="pt-2 text-grey-darken-1 description"
                  >
                    {{ cookieType.description }}
                  </p>
                </v-expand-transition>
              </div>
            </template>

            <template #append>
              <v-btn
                :class="cookieType.showDescription && 'top-aligned'"
                icon
                color="transparent"
                @click.stop="toggleDescription(cookieType.value)"
              >
                <v-icon color="grey lighten-1">mdi-information</v-icon>
              </v-btn>
            </template>
          </v-list-item>
        </v-list>
      </v-card-text>

      <v-divider class="d-none d-sm-block" />

      <v-card-actions class="px-4 py-4 flex-column flex-sm-row">
        <v-btn
          color="primary"
          variant="text"
          @click="acceptSelected"
        >
          {{ t('legal.cookie-consent.buttons.accept-selected') }}
        </v-btn>
        <v-spacer />
        <v-btn
          color="primary"
          data-testid="cookies-submit"
          @click="acceptAll"
        >
          {{ t('legal.cookie-consent.buttons.accept-all') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import * as Sentry from '@sentry/browser'
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

// Import the diagnostic function
import { captureCookieEnvironmentInfo } from '@/lib/cookieHandling'
import { appBranding, dataPrivacy, iubenda } from '@/lib/features'
import IubendaConsentLink from '@/modules/iubenda/ConsentPreferencesLink.vue'
import IubendaDocumentLink from '@/modules/iubenda/DocumentLink.vue'
import { useCookieConsent } from '@/services/cookieConsent'
import { useGTM } from '@/services/tagManager'

const { t } = useI18n()

// Get readable platform name from branding
const readablePlatformName = computed(() => {
  return appBranding?.isActive && appBranding?.config?.readablePlatformName
})

// Get URLs from branding
const publicDataPrivacyUrl = computed(() => {
  return appBranding?.isActive && appBranding?.config?.publicDataPrivacyUrl
})

const imprintUrl = computed(() => {
  return appBranding?.isActive && appBranding?.config?.imprintUrl
})

const useIubenda = computed(() => {
  return iubenda?.isActive
})

const cookieTypes = computed(() => {
  return [
    {
      value: 'mandatory',
      label: t('legal.cookie-consent.cookie-types.mandatory.label'),
      description: t('legal.cookie-consent.cookie-types.mandatory.description', {
        platformName: readablePlatformName.value,
      }),
      readOnly: true,
    },
    {
      value: 'tracking',
      label: t('legal.cookie-consent.cookie-types.tracking.label'),
      description: t('legal.cookie-consent.cookie-types.tracking.description', {
        platformName: readablePlatformName.value,
      }),
    },
  ].map((type) => ({
    ...type,
    showDescription: false,
  }))
})

const cookieConsent = useCookieConsent()
const { enableGTM } = useGTM()

const showDialog = ref(false)
const choice = ref(['mandatory'])
const cookieTypesList = ref(cookieTypes)

const useLegacyCookieConsent = computed(() => {
  return dataPrivacy?.isActive
})

const handleCheckboxChange = (cookieType) => {
  try {
    if (cookieType.readOnly) {
      return
    }

    if (cookieType.value === 'tracking') {
      const isSelected = choice.value.includes(cookieType.value)
      enableGTM(isSelected)
    }
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        component: 'CookieConsent',
        action: 'handleCheckboxChange',
      },
      extra: {
        cookieType: JSON.stringify(cookieType),
      },
    })
    // Don't stop execution - just log the error
  }
}

const toggleDescription = (cookieTypeValue) => {
  try {
    cookieTypesList.value = cookieTypesList.value.map((item) => ({
      ...item,
      showDescription:
        item.value === cookieTypeValue ? !item.showDescription : item.showDescription,
    }))
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        component: 'CookieConsent',
        action: 'toggleDescription',
      },
      extra: {
        cookieTypeValue,
      },
    })
    // Don't break the UI on toggle error
  }
}

const accept = (selectedChoice) => {
  try {
    cookieConsent.accept(selectedChoice)
    showDialog.value = false
    const hasTrackingConsent = selectedChoice.includes('tracking')
    enableGTM(hasTrackingConsent)

    if (
      hasTrackingConsent &&
      window.loadMatomoScript &&
      typeof window.loadMatomoScript === 'function'
    ) {
      try {
        window.loadMatomoScript()
      } catch (error) {
        Sentry.captureException(error, {
          tags: {
            component: 'CookieConsent',
            action: 'loadMatomo',
          },
        })
      }
    }
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        component: 'CookieConsent',
        action: 'accept',
      },
      extra: {
        selectedChoice: JSON.stringify(selectedChoice),
      },
    })
    // Fallback - close dialog and enable mandatory only
    showDialog.value = false
    try {
      document.cookie = 'cookieConsentChoice=["mandatory"]; path=/; max-age=31536000'
    } catch (e) {
      Sentry.captureException(e, {
        tags: {
          component: 'CookieConsent',
          action: 'accept-fallback',
        },
        level: 'fatal',
      })
    }
  }
}

const acceptAll = () => {
  try {
    const allChoices = cookieTypesList.value.map((ct) => ct.value)
    accept(allChoices)
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        component: 'CookieConsent',
        action: 'acceptAll',
      },
      extra: {
        cookieTypesList: JSON.stringify(cookieTypesList.value),
      },
    })
    // Fallback
    showDialog.value = false
  }
}

const acceptSelected = () => {
  try {
    accept(choice.value)
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        component: 'CookieConsent',
        action: 'acceptSelected',
      },
      extra: {
        choice: JSON.stringify(choice.value),
      },
    })
    // Fallback
    showDialog.value = false
  }
}

onMounted(() => {
  // Add browser information to Sentry for better error context
  Sentry.setContext('browser', {
    userAgent: navigator.userAgent,
    language: navigator.language,
    cookiesEnabled: navigator.cookieEnabled,
    doNotTrack: navigator.doNotTrack,
    platform: navigator.platform,
    vendor: navigator.vendor,
  })

  // Log cookie consent initialization to help with debugging
  Sentry.addBreadcrumb({
    category: 'cookieConsent',
    message: 'Cookie consent component mounted',
    level: 'info',
    data: {
      hasExistingConsent: cookieConsent.hasAccepted(),
      isOnoffice: Boolean(sessionStorage.getItem('onOffice')),
      url: window.location.href,
    },
  })

  // Capture initial cookie environment info
  const cookieInfo = captureCookieEnvironmentInfo()
  Sentry.setContext('cookieConsentInfo', cookieInfo)

  // Check for auto-accept query parameter
  const urlParams = new URLSearchParams(window.location.search)
  const autoAcceptParam = urlParams.get('auto_accept_cookies')

  if (autoAcceptParam === 'all') {
    try {
      // Auto-accept all cookies
      const allCookieTypes = cookieTypesList.value.map((type) => type.value)
      cookieConsent.accept(allCookieTypes)
      enableGTM(true)

      // Log this auto-acceptance to Sentry for monitoring
      Sentry.addBreadcrumb({
        category: 'cookieConsent',
        message: 'Cookies auto-accepted via URL parameter',
        level: 'info',
        data: {
          paramValue: autoAcceptParam,
          acceptedTypes: allCookieTypes,
        },
      })

      return // Skip showing the dialog completely
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          component: 'CookieConsent',
          action: 'autoAcceptFromUrl',
        },
      })
      // Continue with normal flow if auto-accept fails
    }
  } else if (autoAcceptParam === 'mandatory') {
    try {
      // Auto-accept only mandatory cookies
      cookieConsent.accept(['mandatory'])

      // Log this auto-acceptance to Sentry for monitoring
      Sentry.addBreadcrumb({
        category: 'cookieConsent',
        message: 'Mandatory cookies auto-accepted via URL parameter',
        level: 'info',
        data: { paramValue: autoAcceptParam },
      })

      return // Skip showing the dialog completely
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          component: 'CookieConsent',
          action: 'autoAcceptMandatoryFromUrl',
        },
      })
      // Continue with normal flow if auto-accept fails
    }
  }

  const accepted = cookieConsent.hasAccepted()
  const isOnoffice = Boolean(sessionStorage.getItem('onOffice'))
  if (!(
    accepted ||
    isOnoffice ||
    window.location.href.match(/onoffice\/(activate|service)/) ||
    window.location.href.includes('/products')
  )) {
    showDialog.value = true
  }

  if (accepted) {
    enableGTM(cookieConsent.hasTrackingConsent())

    if (
      cookieConsent.hasTrackingConsent() &&
      window.loadMatomoScript &&
      typeof window.loadMatomoScript === 'function'
    ) {
      try {
        window.loadMatomoScript()
      } catch (error) {
        Sentry.captureException(error, {
          tags: {
            component: 'CookieConsent',
            action: 'loadMatomoOnMount',
          },
        })
      }
    }
  }
})
</script>

<style scoped>
.top-aligned {
  align-self: flex-start;
}

.description {
  line-height: inherit;
}
.checkbox-list :deep(.v-input--horizontal) {
  grid-template-areas: none;
}
</style>
